import { usePageMeta } from '../lib/usePageMeta.js'
import { SITE } from '../lib/site.js'
import { FAQS, PLANS, SERVICES } from '../lib/content.js'
import { Icon } from '../components/icons.jsx'
import {
  Button,
  CheckList,
  CtaBand,
  IconTile,
  PageHero,
  Section,
  SectionHead,
} from '../components/ui.jsx'

function ServiceBlocks() {
  return (
    <Section id="what-we-do" className="!py-6 sm:!py-10">
      {SERVICES.map((s, i) => (
        <article
          key={s.slug}
          id={s.slug}
          className="grid scroll-mt-[88px] items-center gap-y-8 border-b border-line py-12 last:border-0 sm:py-16 lg:grid-cols-2 lg:gap-x-16"
        >
          <div className={`flex flex-col items-start gap-y-5 ${i % 2 ? 'lg:order-2' : ''}`}>
            <IconTile name={s.icon} />
            <h2 className="text-[28px] font-bold leading-[1.15] tracking-[-0.02em] text-ink sm:text-[34px]">
              {s.title}
            </h2>
            <p className="text-[17px] leading-[1.6] text-dim">{s.body}</p>
            <Button to="/contact" variant="ghost" icon="arrowRight">
              Talk about {s.title.toLowerCase()}
            </Button>
          </div>
          <div className={`rounded-[16px] border border-line bg-surface p-7 sm:p-8 ${i % 2 ? 'lg:order-1' : ''}`}>
            <div className="mb-5 text-[12px] font-bold uppercase tracking-[0.14em] text-faint">
              What is included
            </div>
            <CheckList items={s.includes} />
          </div>
        </article>
      ))}
    </Section>
  )
}

function Plans() {
  return (
    <Section id="plans" tone="surface">
      <SectionHead
        eyebrow="Plans"
        title="Fixed monthly prices. No long-term contracts."
        body="Every plan starts with the free audit and a written 90-day forecast. Ad spend is paid straight to Google."
      />
      <div className="mt-14 grid gap-6 lg:grid-cols-3">
        {PLANS.map((p) => (
          <div
            key={p.name}
            className={`relative flex flex-col gap-y-6 rounded-[16px] border p-8 ${
              p.featured
                ? 'border-primary bg-night text-white shadow-lift'
                : 'border-line bg-white shadow-card'
            }`}
          >
            {p.featured ? (
              <span className="absolute -top-3 left-8 rounded-full bg-primary px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-white">
                Most popular
              </span>
            ) : null}
            <div className="flex flex-col gap-y-2">
              <span
                className={`text-[13px] font-bold uppercase tracking-[0.14em] ${p.featured ? 'text-[#ffb27a]' : 'text-primary-dark'}`}
              >
                {p.name}
              </span>
              <div className="flex items-baseline gap-x-2">
                <span className="text-[40px] font-extrabold leading-none tracking-[-0.03em]">
                  {p.price}
                </span>
                <span className={`text-[14px] ${p.featured ? 'text-[#b6bfd0]' : 'text-dim'}`}>
                  {p.period}
                </span>
              </div>
              <p className={`text-[15px] ${p.featured ? 'text-[#dbe1ec]' : 'text-dim'}`}>{p.blurb}</p>
            </div>
            <CheckList items={p.features} tone={p.featured ? 'white' : 'ink'} />
            <div className="mt-auto flex flex-col gap-y-3 pt-2">
              <Button to="/contact" variant={p.featured ? 'primary' : 'dark'} icon="arrowRight">
                {p.cta}
              </Button>
              {p.note ? (
                <span className={`text-[12px] ${p.featured ? 'text-[#8b95a9]' : 'text-faint'}`}>
                  {p.note}
                </span>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </Section>
  )
}

function Faq() {
  return (
    <Section id="faq">
      <div className="grid gap-y-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-x-16">
        <SectionHead
          align="left"
          eyebrow="FAQ"
          title="Straight answers."
          body="The questions every owner asks on the first call."
        />
        <div className="flex flex-col divide-y divide-line rounded-[16px] border border-line bg-white px-7 shadow-card">
          {FAQS.map((f) => (
            <details key={f.q} className="group py-5">
              <summary className="flex cursor-pointer items-center justify-between gap-x-4 text-[17px] font-semibold text-ink">
                {f.q}
                <Icon
                  name="chevronDown"
                  size={18}
                  className="shrink-0 text-faint transition-transform group-open:rotate-180"
                />
              </summary>
              <p className="mt-3 text-[15px] leading-[1.6] text-dim">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </Section>
  )
}

export default function Services() {
  usePageMeta(
    'Services',
    'Contractor websites, local SEO, Google Ads and Local Services Ads, review systems, lead follow-up and brand content. Fixed monthly plans, no long-term contracts.',
  )
  return (
    <>
      <PageHero
        eyebrow="Services"
        title="Marketing built for how contractors actually get hired."
        body="A homeowner searches, compares three companies in ninety seconds, and calls the one that looks legit and answers first. Every service below is built around that moment."
      />
      <ServiceBlocks />
      <Plans />
      <Faq />
      <CtaBand phone={{ href: SITE.phoneHref, label: SITE.phone }} />
    </>
  )
}
